import { combineReducers, configureStore, type Reducer } from "@reduxjs/toolkit"
import { useDispatch, useSelector, useStore } from "react-redux"

import { initializeSharedConfig } from "@/common/boot"
import { getAuthToken } from "@/common/services/auth"
import { contactsViewReducer } from "@/modules/Contacts/store/contactsViewSlice"
import { conversationsReducer } from "@/modules/Conversations/store/conversationsSlice"
import deploymentFormReducer from "@/modules/Deployments/store/deploymentFormSlice"
import { embedChatReducer } from "@/modules/EmbedChat/store"
import { emulatorReducer } from "@/modules/Emulator/store/emulatorSlice"
import { imageUploadReducer } from "@/modules/ImageUpload/store/imageUploadSlice"
import { playgroundChatReducer } from "@/modules/Playground/store/slice"
// Both imports below are from the two workflow-store modules that DO NOT reach
// `@xyflow/react` at runtime: `state` imports only types, and `types` is types
// only. Reaching for `./slice` or `./selectors` here is what put the canvas
// library in the entry — see `placeholderWorkflowReducer`.
import { createInitialState } from "@/modules/Workflow/store/state"
import type { WorkflowState } from "@/modules/Workflow/store/types"
import authReducer from "@/store/auth/authSlice"
import { deferredUndoMiddleware, installUndoMiddleware } from "@/store/deferred-undo-middleware"
import guiReducer from "@/store/gui/guiSlice"
import { createWebSocketMiddleware } from "@/store/websocket/websocketMiddleware"
import { websocketReducer } from "@/store/websocket/websocketSlice"
import { onWorkflowStoreRegistered } from "@/store/workflow-slot"

initializeSharedConfig()

/**
 * Holds the `workflow` key until the canvas chunk arrives.
 *
 * 🚨 THE STORE IS EAGER ON EVERY ROUTE, INCLUDING THE PUBLIC `/embed/:embed-id`
 * WIDGET, AND THE WORKFLOW SLICE IS THE ONLY MEMBER THAT DRAGS A VENDOR.
 * `modules/Workflow/store/reducers/workflow` imports `addEdge`,
 * `applyEdgeChanges` and `applyNodeChanges` from `@xyflow/react` as VALUES, so
 * naming `workflowReducer` here made the 172 kB `xyflow` chunk AND its 20 kB
 * stylesheet static dependencies of the entry — measured in `index.html`'s
 * modulepreload set, for a route that never renders a canvas.
 *
 * It returns the slice's REAL initial state rather than `{}` or `undefined`, so
 * `state.workflow` is a valid `WorkflowState` from the first dispatch and no
 * selector has to learn a new nullable shape. That keeps `RootState` complete
 * and is why this change ripples into no console code: `state.workflow.flow` is
 * `null` before the canvas loads, which is exactly what it was before.
 *
 * `modules/Workflow/store/slice` replaces it on its own module evaluation, so
 * nothing has to remember to call the injector.
 */
const placeholderWorkflowReducer: Reducer<WorkflowState> = (state = createInitialState()) => state

const staticReducers = {
  playgroundChat: playgroundChatReducer,
  embedChat: embedChatReducer,
  websocket: websocketReducer,
  auth: authReducer,
  gui: guiReducer,
  deploymentForm: deploymentFormReducer,
  emulator: emulatorReducer,
  imageUpload: imageUploadReducer,
  workflow: placeholderWorkflowReducer,
  contactsView: contactsViewReducer,
  conversations: conversationsReducer
}

export const store = configureStore({
  reducer: staticReducers,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // `payload.chats` is a chat read on its way into `playgroundChat.chats`,
        // ignored below for the same reason: its dates arrive as `Date`.
        ignoredActionsPaths: ["meta.arg", "payload.timestamp", "payload.file", "payload.chats"],
        ignoredPaths: [
          "playgroundChat.chats",
          "embedChat.chats",
          "imageUpload.previewUrl",
          "workflow.flow",
          // Undo/redo snapshots hold the same node/edge objects as workflow.flow
          "workflow.history",
          "deploymentForm.meta.agentWithSkills"
        ]
      }
    }).concat(
      // The `/chat-streaming` handshake is identified server-side from a real
      // Clerk JWT. `getAuthToken` is the same non-React accessor the HTTP
      // layer uses, so the socket and the API authenticate as one identity.
      // It returns null on the public `/embed/:id` route, which has no Clerk —
      // that visitor is identified by the deployment's agent id instead.
      createWebSocketMiddleware({ getAuthToken }),
      deferredUndoMiddleware
    )
})

// Mounts the real workflow slice and its undo middleware the moment
// `modules/Workflow/store/slice` evaluates — which happens whenever any part of
// the canvas loads. There is no call site to forget, and the slice registers
// into the leaf slot rather than importing this module, so a pure reducer unit
// test that imports the slice does not boot the root store.
onWorkflowStoreRegistered(({ reducer, resolveUndoMiddleware }) => {
  installUndoMiddleware(resolveUndoMiddleware)
  store.replaceReducer(combineReducers({ ...staticReducers, workflow: reducer }))
})

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
export type AppStore = typeof store

export const useAppDispatch = useDispatch.withTypes<AppDispatch>()
export const useAppSelector = useSelector.withTypes<RootState>()

/**
 * The store itself, for reads that must happen at CALL time rather than at
 * render time.
 *
 * `useAppSelector` gives a value captured by the render that read it, which is
 * what a rendering component wants and what an imperative handler must not
 * rely on. A handler that dispatches and then calls a `useCallback` closing
 * over selector output runs entirely inside one event: React has not
 * re-rendered, so that callback still holds the PRE-dispatch value and sends
 * it. `useAppStore().getState()` reads what the reducer just wrote.
 *
 * Reach for this only in event handlers and effects. In render, use
 * `useAppSelector` — `getState()` does not subscribe and will not re-render.
 */
export const useAppStore = useStore.withTypes<AppStore>()
